/**
 * FrameBudgetController — Adaptive frame budget based on real frame time.
 *
 * Tracks an exponential moving average (EMA) of frame durations and adjusts
 * how many video captures and effect passes are allowed per frame.
 *
 * Priority system: videos are sorted by screen area and selection state.
 * Low-priority videos freeze on their last captured frame when the budget
 * is exhausted — visually unnoticeable at small sizes.
 */

import type { FrameBudget, VideoEntry } from "./VideoRegistry";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** EMA smoothing factor (0.15 = responsive to recent frames, stable enough to avoid jitter) */
const EMA_ALPHA = 0.15;

/** Budget thresholds in milliseconds */
const BUDGET_FULL_MS = 14;      // Under 14ms → full quality
const BUDGET_BALANCED_MS = 20;  // 14-20ms → balanced
// Above 20ms → draft/emergency

/** Progressive quality scale constants */
const QUALITY_SCALE_FLOOR = 0.5;
const QUALITY_SCALE_CEIL = 1.0;
const QUALITY_EMA_LOW = 14;   // ms — full quality
const QUALITY_EMA_HIGH = 26;  // ms — minimum quality
const QUALITY_STEP = 0.1;     // snap to nearest 10%

/** Capture limits per tier */
const FULL_MAX_CAPTURES = 6;
const FULL_MAX_EFFECTS = 6;
const BALANCED_MAX_CAPTURES = 4;
const BALANCED_MAX_EFFECTS = 3;
const DRAFT_MAX_CAPTURES = 2;
const DRAFT_MAX_EFFECTS = 1;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _emaFrameMs = 8; // Start optimistic

// Dropped frame detection — a frame is "dropped" when the gap between
// consecutive ticks exceeds 1.5× the expected 60fps interval (~25ms).
const EXPECTED_FRAME_MS = 16.667; // 1000/60
const DROP_THRESHOLD_MS = EXPECTED_FRAME_MS * 1.5; // ~25ms
let _lastSampleTs = 0;
let _droppedFrames = 0;

// Ring buffer for real-time HUD telemetry. Fixed-capacity Float32/64Array
// so there is zero allocation per frame on the playback hot path.
const SAMPLE_CAPACITY = 240; // ~4s at 60fps, covers slow/draft playback too
const _frameSamplesMs = new Float32Array(SAMPLE_CAPACITY);
const _frameSampleTsMs = new Float64Array(SAMPLE_CAPACITY);
let _sampleWriteIdx = 0;
let _sampleCount = 0;

function _nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

export interface RenderFrameStats {
  /** Number of samples inside the requested window. */
  count: number;
  /** Wall-clock span (ms) of the samples returned. 0 when no samples. */
  windowMs: number;
  /** count / (windowMs/1000) — how many frames actually rendered per second. */
  avgFps: number;
  /** Exponential moving average of frame ms (same value used by the frame budget). */
  emaMs: number;
  minMs: number;
  maxMs: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  /** Cumulative frames dropped since last reset (gap between ticks > 1.5× expected). */
  droppedFrames: number;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Feed a frame duration sample into the EMA tracker and the ring buffer.
 * Call this at the END of each RenderCoordinator tick with the measured frame time.
 */
export function sampleFrameTime(frameMs: number): void {
  const now = _nowMs();
  // Detect dropped frames: gap since last tick > 1.5× expected interval
  if (_lastSampleTs > 0) {
    const gap = now - _lastSampleTs;
    if (gap > DROP_THRESHOLD_MS) {
      // Estimate how many frames were missed (minus the one we're rendering now)
      _droppedFrames += Math.max(1, Math.round(gap / EXPECTED_FRAME_MS) - 1);
    }
  }
  _lastSampleTs = now;

  _emaFrameMs = _emaFrameMs * (1 - EMA_ALPHA) + frameMs * EMA_ALPHA;
  _frameSamplesMs[_sampleWriteIdx] = frameMs;
  _frameSampleTsMs[_sampleWriteIdx] = now;
  _sampleWriteIdx = (_sampleWriteIdx + 1) % SAMPLE_CAPACITY;
  if (_sampleCount < SAMPLE_CAPACITY) _sampleCount++;
}

/**
 * Get the current EMA frame time (for telemetry/debug).
 */
export function getEmaFrameMs(): number {
  return _emaFrameMs;
}

/**
 * Return rolling-window stats for the render coordinator tick.
 * Used by the Performance HUD. Pulls from the ring buffer, computes
 * percentiles on demand — only called at ~2 Hz from the HUD.
 */
export function getRenderFrameStats(windowMs: number = 2000): RenderFrameStats {
  if (_sampleCount === 0) {
    return {
      count: 0,
      windowMs: 0,
      avgFps: 0,
      emaMs: _emaFrameMs,
      minMs: 0,
      maxMs: 0,
      p50Ms: 0,
      p95Ms: 0,
      p99Ms: 0,
      droppedFrames: _droppedFrames,
    };
  }
  const nowMs = _nowMs();
  const cutoff = nowMs - Math.max(100, windowMs);
  const tmp: number[] = [];
  let oldestInWindowTs = nowMs;
  for (let i = 0; i < _sampleCount; i++) {
    const idx = (_sampleWriteIdx - 1 - i + SAMPLE_CAPACITY) % SAMPLE_CAPACITY;
    const ts = _frameSampleTsMs[idx];
    if (ts < cutoff) break;
    tmp.push(_frameSamplesMs[idx]);
    oldestInWindowTs = ts;
  }
  if (tmp.length === 0) {
    return {
      count: 0,
      windowMs: 0,
      avgFps: 0,
      emaMs: _emaFrameMs,
      minMs: 0,
      maxMs: 0,
      p50Ms: 0,
      p95Ms: 0,
      p99Ms: 0,
    };
  }
  tmp.sort((a, b) => a - b);
  const pick = (q: number) =>
    tmp[Math.min(tmp.length - 1, Math.max(0, Math.floor(q * (tmp.length - 1))))];
  const spanMs = Math.max(1, nowMs - oldestInWindowTs);
  return {
    count: tmp.length,
    windowMs: spanMs,
    avgFps: Math.round((tmp.length * 1000) / spanMs),
    emaMs: _emaFrameMs,
    minMs: tmp[0],
    maxMs: tmp[tmp.length - 1],
    p50Ms: pick(0.5),
    p95Ms: pick(0.95),
    p99Ms: pick(0.99),
    droppedFrames: _droppedFrames,
  };
}

/**
 * Compute the frame budget for this tick based on current EMA.
 */
export function computeFrameBudget(): FrameBudget {
  if (_emaFrameMs <= BUDGET_FULL_MS) {
    return {
      maxCaptures: FULL_MAX_CAPTURES,
      maxEffectPasses: FULL_MAX_EFFECTS,
      qualityTier: "full",
    };
  }
  if (_emaFrameMs <= BUDGET_BALANCED_MS) {
    return {
      maxCaptures: BALANCED_MAX_CAPTURES,
      maxEffectPasses: BALANCED_MAX_EFFECTS,
      qualityTier: "balanced",
    };
  }
  return {
    maxCaptures: DRAFT_MAX_CAPTURES,
    maxEffectPasses: DRAFT_MAX_EFFECTS,
    qualityTier: "draft",
  };
}

/**
 * Compute priority for a video entry. Lower number = higher priority.
 * - 0: Selected by user (hero element)
 * - 1: Large on screen (>500k px²)
 * - 2: Medium (>100k px²)
 * - 3: Small (>10k px²)
 * - 4: Tiny or offscreen
 */
export function computeVideoPriority(
  entry: VideoEntry,
  selectedIds: ReadonlySet<string>,
): number {
  if (selectedIds.has(entry.elementId)) return 0;
  if (entry.screenArea > 500_000) return 1;
  if (entry.screenArea > 100_000) return 2;
  if (entry.screenArea > 10_000) return 3;
  return 4;
}

/**
 * Compute a continuous quality scale from 0.5 to 1.0 based on the
 * current EMA frame time. Snapped to 10% steps to avoid constant
 * micro-resizes. The existing discrete tier system (computeFrameBudget)
 * remains unchanged — this is an ADDITIONAL export for consumers that
 * want smooth scaling (e.g., canvas resolution).
 */
export function computeQualityScale(emaMs: number): number {
  const clamped = Math.max(QUALITY_EMA_LOW, Math.min(QUALITY_EMA_HIGH, emaMs));
  const raw = QUALITY_SCALE_CEIL -
    ((clamped - QUALITY_EMA_LOW) / (QUALITY_EMA_HIGH - QUALITY_EMA_LOW)) *
    (QUALITY_SCALE_CEIL - QUALITY_SCALE_FLOOR);
  return Math.round(raw / QUALITY_STEP) * QUALITY_STEP;
}

/**
 * Get the cumulative dropped frame count since last reset.
 */
export function getDroppedFrames(): number {
  return _droppedFrames;
}

/**
 * Reset EMA and dropped frame counter (e.g. on editor mount or playback start).
 */
export function resetFrameBudget(): void {
  _emaFrameMs = 8;
  _droppedFrames = 0;
  _lastSampleTs = 0;
}
